import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle, Loader2, Send } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import PageHero from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import SectionLabel from "@/components/common/SectionLabel";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import client, { formatApiError } from "@/lib/api";
import { COMPANY } from "@/data/site";

const inputCls = "bg-gema-surface border-white/15 text-white placeholder:text-white/30 rounded-none focus-visible:ring-gema-red";
const EMPTY = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useSEO({
    title: "Contacto",
    description: "Contacta a GEMA para mantenimiento automotriz, industrial y naval. Teléfono, WhatsApp, correo y ubicación en Turbaco, Bolívar, Colombia.",
    path: "/contacto",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Ingresa tu nombre";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Correo inválido";
    if (!form.message.trim()) e.message = "Escribe tu mensaje";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await client.post("/contact", form);
      toast.success("Mensaje enviado", { description: "Gracias por contactarnos. Te responderemos pronto." });
      setForm(EMPTY);
    } catch (err) {
      toast.error("No se pudo enviar", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  const info = [
    { icon: Phone, label: "Teléfono", value: COMPANY.phone, href: `tel:${COMPANY.phoneRaw}` },
    { icon: Mail, label: "Correo", value: COMPANY.email, href: `mailto:${COMPANY.email}` },
    { icon: MapPin, label: "Ubicación", value: COMPANY.address },
    { icon: MessageCircle, label: "WhatsApp", value: COMPANY.phone, href: COMPANY.whatsapp, external: true },
  ];

  return (
    <>
      <PageHero
        overline="Contacto"
        title="Hablemos de tu próximo proyecto"
        subtitle="Estamos listos para asesorarte. Escríbenos y recibe atención personalizada para tu vehículo, equipo o proyecto."
        crumbs={[{ label: "Contacto" }]}
      />

      <section className="bg-gema-black py-20 md:py-28">
        <div className="container-gema grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal><SectionLabel>Formulario</SectionLabel></Reveal>
            <Reveal delay={0.1}><h2 className="heading-lg text-white mt-6 mb-8">Envíanos un mensaje</h2></Reveal>
            <Reveal delay={0.2}>
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form" noValidate>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-white/70 text-xs uppercase tracking-wider">Nombre *</Label>
                    <Input data-testid="contact-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
                    {errors.name && <p className="text-gema-red text-xs mt-1">{errors.name}</p>}
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs uppercase tracking-wider">Teléfono</Label>
                    <Input data-testid="contact-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-white/70 text-xs uppercase tracking-wider">Correo *</Label>
                    <Input data-testid="contact-email" type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} />
                    {errors.email && <p className="text-gema-red text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label className="text-white/70 text-xs uppercase tracking-wider">Asunto</Label>
                    <Input data-testid="contact-subject" className={inputCls} value={form.subject} onChange={(e) => set("subject", e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label className="text-white/70 text-xs uppercase tracking-wider">Mensaje *</Label>
                  <Textarea data-testid="contact-message" rows={5} className={inputCls} value={form.message} onChange={(e) => set("message", e.target.value)} />
                  {errors.message && <p className="text-gema-red text-xs mt-1">{errors.message}</p>}
                </div>
                <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-primary disabled:opacity-60">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Enviar Mensaje</>}
                </button>
              </form>
            </Reveal>
          </div>

          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                {info.map((it) => {
                  const Icon = it.icon;
                  const content = (
                    <div className="bg-gema-ink p-6 h-full">
                      <Icon className="h-6 w-6 text-gema-red" strokeWidth={1.5} />
                      <p className="font-mono text-[10px] uppercase tracking-widest text-white/40 mt-4">{it.label}</p>
                      <p className="text-white text-sm mt-1 break-words">{it.value}</p>
                    </div>
                  );
                  return it.href ? (
                    <a key={it.label} href={it.href} target={it.external ? "_blank" : undefined} rel="noopener noreferrer" className="transition-colors hover:bg-gema-surface">{content}</a>
                  ) : (
                    <div key={it.label}>{content}</div>
                  );
                })}
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="border border-white/10 p-6 mt-6">
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-gema-red" />
                  <h3 className="font-heading font-bold uppercase text-white text-sm">Horario de Atención</h3>
                </div>
                <ul className="mt-4 space-y-2">
                  {COMPANY.hours.map((h) => (
                    <li key={h.day} className="flex justify-between text-sm">
                      <span className="text-white/50">{h.day}</span>
                      <span className="text-white/80">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="bg-gema-ink pb-20 md:pb-28">
        <div className="container-gema">
          <Reveal>
            <div className="border border-white/10 overflow-hidden" data-testid="contact-map">
              <iframe
                title="Ubicación GEMA"
                src="https://www.google.com/maps?q=Turbaco,+Bol%C3%ADvar,+Colombia&output=embed"
                width="100%"
                height="420"
                style={{ border: 0, filter: "grayscale(1) invert(0.92) contrast(0.9)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
